import { type FC, memo, useEffect, useState } from 'react';
import { Link } from 'react-router';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  ButtonBase,
  Divider,
  Grid,
  Stack,
  Typography,
} from '@mui/material';
import {
  ChevronRight,
  ExpandMore,
  ImageNotSupported,
  LightbulbOutlined,
} from '@mui/icons-material';

import { type FileContent } from '#apps/client-web/features/file-storage/services/file-storage';
import { LightingTypeEntryDetails } from '#apps/client-web/features/audit-form-lighting-type/components/lighting-type-entry-details';

import { type AuditLightingTypeFormEntry } from '#packages/data-collection/models/audit-lighting-type-form';

const THUMB_SIZE = 72;

export type LightingTypeListEntry = {
  link: string;
  entry: AuditLightingTypeFormEntry;
  thumbnail: FileContent | null;
};

export type LightSourceGroup = {
  id: string;
  label: string;
  entries: LightingTypeListEntry[];
};

export type AuditFormLightingTypePageRendererProps = {
  groups: LightSourceGroup[];
};

const EntryThumbnail: FC<{ content: FileContent | null }> = ({ content }) => {
  const [src, setSrc] = useState<string | null>(null);

  useEffect(() => {
    if (!content) {
      setSrc(null);
      return;
    }
    const blob = content instanceof Blob ? content : new Blob([content]);
    const url = URL.createObjectURL(blob);
    setSrc(url);
    return () => URL.revokeObjectURL(url);
  }, [content]);

  return (
    <Box
      sx={(theme) => ({
        width: THUMB_SIZE,
        height: THUMB_SIZE,
        flexShrink: 0,
        borderRadius: 1,
        overflow: 'hidden',
        bgcolor: theme.palette.action.hover,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        mr: 2,
      })}>
      {src ? (
        <Box
          component="img"
          src={src}
          sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
        />
      ) : (
        <ImageNotSupported
          sx={(theme) => ({ color: theme.palette.action.disabled })}
        />
      )}
    </Box>
  );
};

export const AuditFormLightingTypePageRenderer: FC<AuditFormLightingTypePageRendererProps> =
  memo(({ groups }) => (
    <Box flexGrow={1}>
      {groups.map((group) => (
        <Accordion key={group.id} variant="outlined">
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <LightbulbOutlined fontSize="small" />
              <Typography variant="body1">{group.label}</Typography>
            </Stack>
          </AccordionSummary>
          <AccordionDetails>
            <Stack>
              {group.entries.map(({ link, entry, thumbnail }, index) => (
                <ButtonBase
                  key={link}
                  component={Link}
                  to={link}
                  sx={{ display: 'block', textAlign: 'start' }}>
                  <Grid container alignItems="center" sx={{ p: 2 }}>
                    <Grid>
                      <EntryThumbnail content={thumbnail} />
                    </Grid>
                    <Grid size="grow">
                      <LightingTypeEntryDetails entry={entry} />
                    </Grid>
                    <Grid>
                      <ChevronRight
                        sx={(theme) => ({ color: theme.palette.grey[400] })}
                      />
                    </Grid>
                  </Grid>
                  {index < group.entries.length - 1 && <Divider />}
                </ButtonBase>
              ))}
            </Stack>
          </AccordionDetails>
        </Accordion>
      ))}
    </Box>
  ));
